import { Loader } from "@circle-react-uikit/Loader";
import { BotChatItem } from "@circle-react/components/Modals/MessagingModalCompass/LeftSidebar/List/BotChatItem";
import { usePinnedBotChat } from "@circle-react/components/SettingsApp/CommunityInbox/hooks/usePinnedBotChat";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useChatRoomChannelWebSocket } from "@circle-react/hooks/chatsV2/useChatRoomChannelWebSocket";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { useChatsSidebarApi } from "@/react/hooks/chatsV2/sidebar/useChatsSidebarApi";

import { ChatRoomsList } from "./ChatRoomsList";
import { ListWrapper } from "./ListWrapper";
import { NoMessages } from "./NoMessages";

const MAX_UNREAD_UUIDS = 2000;

export interface ListProps {
  unreadChatRoomUuids?: string[];
}

export const List = ({ unreadChatRoomUuids }: ListProps) => {
  const { uuid: chatRoomUuid } = useParams({ strict: false });
  const { currentCommunityMember, isLoading: isCommunityLoading } =
    usePunditUserContext();

  const {
    chatRooms,
    hasNextPage,
    isLoading: isChatRoomsLoading,
    onEventReceive,
    fetchNextPage,
  } = useChatsSidebarApi({
    openChatRoomUuid:
      chatRoomUuid && chatRoomUuid !== "new" ? chatRoomUuid : null,
    uuids: unreadChatRoomUuids?.slice(0, MAX_UNREAD_UUIDS),
  });

  useChatRoomChannelWebSocket({
    communityMemberId: currentCommunityMember?.id,
    onMessageReceive: data => {
      onEventReceive(data, currentCommunityMember?.id);
    },
    canCreateConnection: true,
    deps: [chatRooms],
  });

  const { botChat, isLoading: isBotChatLoading } = usePinnedBotChat();
  const shouldShowBotChat = Boolean(botChat);

  const isLoading =
    isCommunityLoading || isChatRoomsLoading || isBotChatLoading;

  const chatRoomWithMessages = useMemo(
    () => chatRooms.filter(chatRoom => chatRoom.last_message),
    [chatRooms],
  );
  const isEmpty = !chatRoomWithMessages?.length && !botChat;

  if (isLoading || !currentCommunityMember) {
    return (
      <ListWrapper>
        <Loader center />
      </ListWrapper>
    );
  }

  if (isEmpty) {
    return (
      <ListWrapper>
        <NoMessages />
      </ListWrapper>
    );
  }

  return (
    <ListWrapper>
      {shouldShowBotChat && <BotChatItem chat={botChat} isRenderedOnHeader />}
      <InfiniteScroll
        style={{ height: "100%", overflow: "hidden", marginTop: "8px" }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={hasNextPage}
        dataLength={chatRoomWithMessages.length}
        scrollableTarget="scrollable-dms-div"
        loader={<Loader center />}
      >
        <ChatRoomsList
          chatRooms={chatRoomWithMessages}
          currentCommunityMember={currentCommunityMember}
        />
      </InfiniteScroll>
    </ListWrapper>
  );
};
